import { createStyles, Grid, IconButton, makeStyles, Theme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import StarIcon from '@material-ui/icons/Star';
import React from 'react';
import { TnSlideInModal } from '../TnSlideInModal';
import { TnTypography } from '../TnTypography';

export interface SkillRatingsLearnMoreDrawerProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '24px 24px 16px',
      gap: 8,
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
    },
    body: {
      padding: '0 24px 16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
    sections: {
      display: 'grid',
      gridTemplateRows: 'repeat(5, auto)',
      rowGap: 16,
      paddingTop: 8,
      width: '100%',
      maxWidth: '100%',
      padding: '0 16px',
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    list: {
      paddingLeft: 16,
      margin: 0,
      '& li': {
        marginBottom: 8,
      },
      '& li:last-child': {
        marginBottom: 0,
      },
    },
    title: {
      fontSize: 40,
      lineHeight: '48px',
      fontWeight: 700,
    },
    levelTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontWeight: 700,
      fontSize: 24,
      lineHeight: '32px',
    },
    starRow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      position: 'relative',
      top: 1,
    },
    star: {
      color: '#f4bf24',
      fontSize: 16,
    },
    bulletText: {
      fontSize: 16,
      lineHeight: '24px',
    },
    boldLabel: {
      fontWeight: 700,
    },
  }),
);

export default function SkillRatingsLearnMoreDrawer({ open, onClose }: Readonly<SkillRatingsLearnMoreDrawerProps>) {
  const classes = useStyles();

  const levels = [
    {
      level: 'Level 1: Novice',
      stars: 1,
      description:
        'Possesses a basic understanding of the skill and requires close supervision. Can follow simple instructions but lacks the ability to apply knowledge independently or adapt to new situations.',
      indicators: 'Minimal hands-on experience, relies on guidelines, and needs support to complete tasks.',
    },
    {
      level: 'Level 2: Beginner',
      stars: 2,
      description:
        'Demonstrates foundational skills and can perform routine tasks with guidance. Understands key concepts but struggles with complex or unfamiliar scenarios.',
      indicators: 'Limited independent work, occasional errors, and moderate reliance on help or supervision.',
    },
    {
      level: 'Level 3: Proficient',
      stars: 3,
      description:
        'Fully capable of applying skills independently in a variety of situations. Understands how to adapt and troubleshoot issues, and performs tasks reliably with minimal oversight.',
      indicators:
        'Consistent performance, effective problem-solving, and an ability to mentor less-experienced individuals.',
    },
    {
      level: 'Level 4: Advanced',
      stars: 4,
      description:
        'Displays a deep understanding and expertise in the skill. Able to handle complex and ambiguous tasks, innovate solutions, and contribute to strategic decision-making.',
      indicators:
        'High level of autonomy, frequent role as a subject matter expert, and ability to train or lead teams in the domain.',
    },
    {
      level: 'Level 5: Expert',
      stars: 5,
      description:
        'Recognized authority in the skill, with mastery over all aspects. Innovates, develops new methodologies, and drives industry standards. Often a go-to resource for the most challenging situations.',
      indicators:
        'Leadership in thought processes, trailblazing innovations, and regular contributions to advancing knowledge in the field.',
    },
  ];

  return (
    <TnSlideInModal
      isOpen={open}
      isLoading={false}
      handleClose={onClose}
      header={{
        headerType: 'custom',
        headerContent: (
          <Grid className={classes.header}>
            <div className={classes.headerTitle}>
              <TnTypography variant="h4" className={classes.title}>
                How Skill Ratings Work
              </TnTypography>
            </div>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        ),
      }}
      footer={{ footerType: 'custom', footerContent: <div /> }}
    >
      <Grid className={classes.body}>
        <div className={classes.sections}>
          {levels.map((item) => (
            <div key={item.level} className={classes.section}>
              <TnTypography variant="body" className={classes.levelTitle}>
                {item.level}
                <span className={classes.starRow}>
                  {Array.from({ length: item.stars }).map((_, idx) => (
                    <StarIcon key={idx} className={classes.star} />
                  ))}
                </span>
              </TnTypography>
              <ul className={classes.list}>
                <li>
                  <TnTypography variant="body" className={classes.bulletText}>
                    <span className={classes.boldLabel}>Description:</span> {item.description}
                  </TnTypography>
                </li>
                <li>
                  <TnTypography variant="body" className={classes.bulletText}>
                    <span className={classes.boldLabel}>Indicators:</span> {item.indicators}
                  </TnTypography>
                </li>
              </ul>
            </div>
          ))}
        </div>
      </Grid>
    </TnSlideInModal>
  );
}
